'use client';

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../api/client';

interface InvitationActionResult {
  message: string;
  invitationId: string;
}

/**
 * Hook for revoking a pending organization invitation.
 * POST /api/v1/organization/invitations/{invitationId}/revocation
 *
 * Keyed by the provider (Clerk) invitation id. The seat-count + pending-member-row
 * cleanup happen via the backend's inbound organizationInvitation.revoked webhook, so
 * the client just invalidates the members + invitations lists on settle.
 */
export function useRevokeInvitation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<InvitationActionResult, Error, string>({
    mutationFn: async (invitationId: string) => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data, error, response } = await apiClient.POST(
        '/api/v1/organization/invitations/{invitationId}/revocation',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            path: {
              invitationId: invitationId,
            },
          },
        }
      );

      if (error || !response.ok || !data) {
        throw new Error('Failed to revoke invitation');
      }

      return {
        message: data.message,
        invitationId: data.invitationId,
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-invitations'],
      });
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}
